import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PlusIcon, BarChart3Icon, MessageSquareIcon, SettingsIcon, PackageIcon, StarIcon, EyeIcon, PencilIcon, CopyIcon, TrashIcon, ChartBarIcon, DollarSignIcon, ShoppingCartIcon, CreditCardIcon, BellIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, ExternalLinkIcon, FileTextIcon, ArrowRightIcon, BanknoteIcon, WalletIcon, CalendarIcon, ClockIcon, DownloadIcon, FilterIcon, SlidersIcon, RefreshCwIcon, SaveIcon, XIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useAuth } from '../components/auth/AuthProvider';
import { getProductsBySeller, deleteProduct, getOrdersBySeller, getPayoutsBySeller, FirestoreProduct, createProduct, updateProduct } from '../firebase/firestore';
import { getNotificationsByUser, markNotificationAsRead } from '../firebase/auth';
import { collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, addDoc, updateDoc, Timestamp, onSnapshot, deleteDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db, storage, auth } from '../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebaseConfig';
import { AnalyticsChart } from '../components/dashboard/AnalyticsChart';
import { MessageCenter } from '../components/dashboard/MessageCenter';
import { DeleteConfirmationModal } from '../components/modals/DeleteConfirmationModal';
// Product type definition
interface Product {
  id: number | string;
  title: string;
  description: string;
  price: number;
  category: string;
  image: string;
  rating: number;
  sales: number;
  revenue: number;
  status: 'published' | 'draft' | 'archived' | 'pending';
  productFileUrl?: string;
}
// Backfill function to ensure all products have updatedAt timestamp
const backfillProductTimestamps = async () => {
  console.log('Starting backfill process for product timestamps...');
  try {
    // Get all products without limit
    const productsRef = collection(db, 'products');
    const productsSnapshot = await getDocs(productsRef);
    const total = productsSnapshot.size;
    let updated = 0;
    let errors = 0;
    console.log(`Found ${total} products to check for updatedAt field`);
    // Process each product
    const updatePromises = productsSnapshot.docs.map(async docSnapshot => {
      const productId = docSnapshot.id;
      const productData = docSnapshot.data();
      try {
        // Check if updatedAt is missing or needs to be converted
        let needsUpdate = false;
        if (!productData.updatedAt) {
          needsUpdate = true;
          console.log(`Product ${productId} is missing updatedAt field`);
        } else if (!(productData.updatedAt instanceof Timestamp) && typeof productData.updatedAt !== 'object') {
          needsUpdate = true;
          console.log(`Product ${productId} has invalid updatedAt field type: ${typeof productData.updatedAt}`);
        }
        if (needsUpdate) {
          // Use createdAt as fallback, or current timestamp if createdAt is also missing
          const timestamp = productData.createdAt || serverTimestamp();
          await updateDoc(doc(db, 'products', productId), {
            updatedAt: timestamp
          });
          console.log(`Updated product ${productId} with new updatedAt timestamp`);
          updated++;
        }
      } catch (err) {
        console.error(`Error updating product ${productId}:`, err);
        errors++;
      }
    });
    // Wait for all update operations to complete
    await Promise.all(updatePromises);
    console.log(`Backfill complete. Updated ${updated} products. Errors: ${errors}`);
    return {
      total,
      updated,
      errors
    };
  } catch (error) {
    console.error('Error in backfill process:', error);
    throw error;
  }
};
// Add this function inside the SellerDashboard component
const runBackfillTimestamps = async () => {
  try {
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    setUpdateError(null);
    console.log('Starting backfill process...');
    const result = await backfillProductTimestamps();
    setUpdateSuccess(`Backfill complete. Updated ${result.updated} products out of ${result.total} total. Errors: ${result.errors}`);
    // Force refresh the products list
    setActiveTab(activeTab === 'products' ? 'products_refresh' : 'products');
    setTimeout(() => {
      setUpdateSuccess(null);
    }, 5000);
  } catch (error) {
    console.error('Error running backfill:', error);
    setUpdateError('Failed to backfill product timestamps. Please try again.');
    setTimeout(() => {
      setUpdateError(null);
    }, 5000);
  } finally {
    setLoading(prev => ({
      ...prev,
      products: false
    }));
  }
};
// Editable product component
const EditableProductCard = ({
  product,
  onSave,
  onCancel,
  isEditing
}: {
  product: Product;
  onSave: (updatedProduct: Product) => void;
  onCancel: () => void;
  isEditing: boolean;
}) => {
  const [formData, setFormData] = useState({
    title: product.title,
    description: product.description,
    price: product.price.toString(),
    category: product.category,
    status: product.status
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Category options
  const categoryOptions = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Other'];
  // Status options
  const statusOptions = [{
    value: 'published',
    label: 'Published'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'archived',
    label: 'Archived'
  }];
  useEffect(() => {
    // Reset form data when product changes
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      status: product.status
    });
    setErrors({});
  }, [product, isEditing]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    } else {
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue < 0) {
        newErrors.price = 'Please enter a valid price';
      }
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }
    return newErrors;
  };
  const handleSubmit = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Create updated product object
    const updatedProduct = {
      ...product,
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price),
      category: formData.category,
      status: formData.status as 'published' | 'draft' | 'archived' | 'pending'
    };
    onSave(updatedProduct);
  };
  return <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" data-id="element-6941">
      <div className="relative aspect-square overflow-hidden" data-id="element-6942">
        <img src={product.image} alt={product.title} className="w-full h-full object-cover" data-id="element-6943" />
        <div className="absolute top-3 left-3" data-id="element-6944">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-6945">
            {product.category}
          </span>
        </div>
        {product.status !== 'published' && <div className="absolute bottom-3 left-3" data-id="element-6946">
            <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${product.status === 'draft' ? 'bg-gray-500' : product.status === 'archived' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-6947">
              {product.status.toUpperCase()}
            </span>
          </div>}
      </div>
      <div className="p-5" data-id="element-6948">
        {isEditing ? <div className="space-y-4" data-id="element-6949">
            <div data-id="element-6950">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6951">
                Title
              </label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.title ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-6952" />
              {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6953">
                  {errors.title}
                </p>}
            </div>
            <div data-id="element-6954">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6955">
                Full Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD] resize-y" placeholder="Describe your product..." data-id="element-6956" />
            </div>
            <div data-id="element-6957">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6958">
                Price (USD)
              </label>
              <div className="relative" data-id="element-6959">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-[#7A8F8A]" data-id="element-6960">
                  $
                </span>
                <input type="text" name="price" value={formData.price} onChange={handleChange} className={`w-full pl-8 pr-4 py-2 border ${errors.price ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-6961" />
              </div>
              {errors.price && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6962">
                  {errors.price}
                </p>}
            </div>
            <div data-id="element-6963">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6964">
                Category
              </label>
              <select name="category" value={formData.category} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.category ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-6965">
                <option value="" data-id="element-6966">Select Category</option>
                {categoryOptions.map(category => <option key={category} value={category} data-id="element-6967">
                    {category}
                  </option>)}
              </select>
              {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6968">
                  {errors.category}
                </p>}
            </div>
            <div data-id="element-6969">
              <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-6970">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.status ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-6971">
                {statusOptions.map(option => <option key={option.value} value={option.value} data-id="element-6972">
                    {option.label}
                  </option>)}
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-6973">
                  {errors.status}
                </p>}
            </div>
            <div className="flex space-x-2 pt-2" data-id="element-6974">
              <button onClick={handleSubmit} className="flex-1 bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center" data-id="element-6975">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-6976" />
                Save
              </button>
              <button onClick={onCancel} className="flex-1 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-6977">
                <XIcon className="h-4 w-4 mr-2" data-id="element-6978" />
                Cancel
              </button>
            </div>
          </div> : <>
            <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9]" data-id="element-6979">
              {product.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-3 line-clamp-2" data-id="element-6980">
              {product.description}
            </p>
            <div className="flex items-center justify-between mb-3" data-id="element-6981">
              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-6982">
                ${product.price}
              </span>
              <div className="flex items-center" data-id="element-6983">
                {product.rating > 0 ? <>
                    <StarIcon className="h-4 w-4 text-yellow-400 fill-current" data-id="element-6984" />
                    <span className="text-sm text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-6985">
                      {product.rating}
                    </span>
                  </> : <span className="text-sm text-gray-400 dark:text-[#7A8F8A]" data-id="element-6986">
                    No ratings yet
                  </span>}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-6987">
              <span data-id="element-6988">Sales: {product.sales}</span>
              <span data-id="element-6989">Revenue: ${product.revenue}</span>
            </div>
          </>}
      </div>
    </div>;
};
// Product card component for seller dashboard
const ProductCard = ({
  product,
  onPreview,
  onEdit,
  onDuplicate,
  onDelete,
  onViewAnalytics,
  onArchive,
  isEditing,
  editingProductId,
  onSaveEdit,
  onCancelEdit
}: {
  product: Product;
  onPreview: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  onDuplicate: (id: number | string) => void;
  onDelete: (id: number | string) => void;
  onViewAnalytics: (id: number | string) => void;
  onArchive: (id: number | string) => void;
  isEditing: boolean;
  editingProductId: string | number | null;
  onSaveEdit: (updatedProduct: Product) => void;
  onCancelEdit: () => void;
}) => {
  const navigate = useNavigate();
  const isEditingThisProduct = isEditing && editingProductId === product.id;
  const [imageLoaded, setImageLoaded] = useState(false);
  // Preload the image
  useEffect(() => {
    if (product.image) {
      const img = new Image();
      img.src = product.image;
      img.onload = () => setImageLoaded(true);
    }
  }, [product.image]);
  if (isEditingThisProduct) {
    return <EditableProductCard product={product} onSave={onSaveEdit} onCancel={onCancelEdit} isEditing={true} data-id="element-6990" />;
  }
  return <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" data-id="element-6991">
      <div className="relative aspect-square overflow-hidden" data-id="element-6992">
        {/* Show a placeholder while image is loading */}
        {!imageLoaded && <div className="absolute inset-0 bg-gray-200 dark:bg-[#1A3330] flex items-center justify-center" data-id="element-6993">
            <div className="animate-pulse w-full h-full" data-id="element-6994"></div>
          </div>}
        <img src={product.image} alt={product.title} className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setImageLoaded(true)} data-id="element-6995" />
        <div className="absolute top-3 left-3" data-id="element-6996">
          <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-6997">
            {product.category}
          </span>
        </div>
        {product.status !== 'published' && <div className="absolute bottom-3 left-3" data-id="element-6998">
            <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${product.status === 'draft' ? 'bg-gray-500' : product.status === 'archived' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-6999">
              {product.status.toUpperCase()}
            </span>
          </div>}
      </div>
      <div className="p-5" data-id="element-7000">
        <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9]" data-id="element-7001">
          {product.title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-3 line-clamp-2" data-id="element-7002">
          {product.description}
        </p>
        <div className="flex items-center justify-between mb-3" data-id="element-7003">
          <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-7004">
            ${product.price}
          </span>
          <div className="flex items-center" data-id="element-7005">
            {product.rating > 0 ? <>
                <StarIcon className="h-4 w-4 text-yellow-400 fill-current" data-id="element-7006" />
                <span className="text-sm text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-7007">
                  {product.rating}
                </span>
              </> : <span className="text-sm text-gray-400 dark:text-[#7A8F8A]" data-id="element-7008">
                No ratings yet
              </span>}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-7009">
          <span data-id="element-7010">Sales: {product.sales}</span>
          <span data-id="element-7011">Revenue: ${product.revenue}</span>
        </div>
        {/* File attachment indicator */}
        {product.productFileUrl && <div className="flex items-center justify-between mb-3 px-2.5 py-2 bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/30 rounded-lg" data-id="element-7012">
            <span className="flex items-center text-xs font-medium text-green-700 dark:text-green-400" data-id="element-7013">
              <FileTextIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-7014" />
              File attached
            </span>
            <a href={product.productFileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs font-medium text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors" data-id="element-7015">
              <DownloadIcon className="h-3.5 w-3.5 mr-1" data-id="element-7016" />
              Download
            </a>
          </div>}
        <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-7017">
          <button onClick={() => onPreview(product.id)} className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-7018">
            <EyeIcon className="h-3.5 w-3.5 mr-1" data-id="element-7019" />
            View as Buyer
          </button>
          <button onClick={() => onEdit(product.id)} className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center" data-id="element-7020">
            <PencilIcon className="h-3.5 w-3.5 mr-1" data-id="element-7021" />
            Edit
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2" data-id="element-7022">
          <button onClick={() => onViewAnalytics(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-7023">
            <ChartBarIcon className="h-3 w-3 mr-1" data-id="element-7024" />
            Analytics
          </button>
          <button onClick={() => onDuplicate(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-7025">
            <CopyIcon className="h-3 w-3 mr-1" data-id="element-7026" />
            Duplicate
          </button>
          {product.status === 'archived' ? <button onClick={() => onDelete(product.id)} className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center col-span-2" data-id="element-7027">
              <TrashIcon className="h-3 w-3 mr-1" data-id="element-7028" />
              Delete
            </button> : <>
              <button onClick={() => onArchive(product.id)} className="bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors flex items-center justify-center" data-id="element-7029">
                <XIcon className="h-3 w-3 mr-1" data-id="element-7030" />
                Archive
              </button>
              <button onClick={() => onDelete(product.id)} className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center" data-id="element-7031">
                <TrashIcon className="h-3 w-3 mr-1" data-id="element-7032" />
                Delete
              </button>
            </>}
        </div>
      </div>
    </div>;
};
export const SellerDashboard = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const {
    isAuthenticated,
    user
  } = useAuth();
  const navigate = useNavigate();
  // Add missing constant declaration
  const [productsUnsubscribe, setProductsUnsubscribe] = useState<(() => void) | null>(null);
  // States for data
  const [sellerProducts, setSellerProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState({
    products: true,
    orders: true,
    payouts: true,
    notifications: true
  });
  // States for filters and sorting
  const [productFilter, setProductFilter] = useState('all');
  const [productSort, setProductSort] = useState('newest');
  const [orderFilter, setOrderFilter] = useState('all');
  const [orderSort, setOrderSort] = useState('newest');
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  // States for modals
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  // States for notification filters
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // States for payment method
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [lastPayoutDate, setLastPayoutDate] = useState('');
  const [lastPayoutAmount, setLastPayoutAmount] = useState(0);
  // States for product editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | number | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  // New state for products error handling
  const [productsError, setProductsError] = useState<string | null>(null);
  const [queryAttempt, setQueryAttempt] = useState(0); // Track query attempts
  // Add new state for retry timeout management
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | null>(null);
  // Add new state to track if component is mounted
  const [isMounted, setIsMounted] = useState(true);
  // Add a constant for maximum retries
  const MAX_RETRY_ATTEMPTS = 2;
  const hasAffiliateRole = user?.roles?.includes('affiliate') || false;
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    // Mark component as mounted
    setIsMounted(true);
    // Reset error and loading states when component mounts or activeTab changes
    setProductsError(null);
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    // Function to fetch products with primary query (using updatedAt)
    const fetchWithPrimaryQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Primary query using updatedAt
        const primaryQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), orderBy('updatedAt', 'desc'), limit(50));
        const unsubscribe = onSnapshot(primaryQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          // Update state with products data
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          setProductsError(null);
          setQueryAttempt(0);
        }, error => {
          if (!isMounted) return;
          console.error('Primary query error:', error);
          // Check if the error is related to missing indexes
          if (error.toString().includes('index')) {
            const indexMatch = error.toString().match(/(https:\/\/console\.firebase\.google\.com\/[^\s]+)/);
            if (indexMatch) {
              console.error('Missing index. Create it here:', indexMatch[0]);
              setProductsError(`Missing Firestore index. Please create the index at: ${indexMatch[0]}`);
            } else {
              setProductsError('Failed to load your products due to a missing index. Please try again later.');
            }
          } else {
            setProductsError('Failed to load your products. Please try again.');
          }
          // Try fallback query with createdAt, but only if we haven't exceeded max retries
          if (queryAttempt < MAX_RETRY_ATTEMPTS) {
            const newAttemptCount = queryAttempt + 1;
            setQueryAttempt(newAttemptCount);
            tryFallbackQuery(sellerId);
          } else {
            trySimpleQuery(sellerId);
          }
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up primary query:', error);
        if (queryAttempt < MAX_RETRY_ATTEMPTS) {
          const newAttemptCount = queryAttempt + 1;
          setQueryAttempt(newAttemptCount);
          tryFallbackQuery(sellerId);
        } else {
          trySimpleQuery(sellerId);
        }
      }
    };
    // Function to try a fallback query if the primary one fails
    const tryFallbackQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Fallback query using createdAt instead of updatedAt
        const fallbackQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), orderBy('createdAt', 'desc'), limit(50));
        const unsubscribe = onSnapshot(fallbackQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          setProductsError(null);
          setQueryAttempt(0);
        }, error => {
          if (!isMounted) return;
          console.error('Fallback query error:', error);
          trySimpleQuery(sellerId);
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up fallback query:', error);
        trySimpleQuery(sellerId);
      }
    };
    // Final fallback with just the sellerId filter, no sorting
    const trySimpleQuery = sellerId => {
      try {
        // Clean up previous listener if it exists
        if (productsUnsubscribe) {
          productsUnsubscribe();
        }
        // Simple query with just the sellerId filter
        const simpleQuery = query(collection(db, 'products'), where('sellerId', '==', sellerId), limit(50));
        const unsubscribe = onSnapshot(simpleQuery, snapshot => {
          if (!isMounted) return;
          const productsData = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            // Calculate sales and revenue from orders
            let sales = 0;
            let revenue = 0;
            orders.forEach(order => {
              if (order.productId === doc.id && order.status === 'paid') {
                sales++;
                revenue += order.amount;
              }
            });
            productsData.push({
              id: doc.id,
              title: data.title || '',
              description: data.shortDescription || data.description || '',
              price: data.price || 0,
              category: data.category || 'Other',
              image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
              rating: data.rating || 0,
              sales: sales,
              revenue: revenue,
              status: data.status || 'published',
              productFileUrl: data.productFileUrl || data.fileUrl || ''
            });
          });
          // Always update products data regardless of warning condition
          setSellerProducts(productsData);
          setLoading(prev => ({
            ...prev,
            products: false
          }));
          // Clear error state regardless of whether we have products or not
          setProductsError(null);
          // Only show backfill message if we have products with missing timestamps
          if (productsData.length > 0) {
            setUpdateSuccess('Products loaded successfully. For better sorting, you may run the backfill function.');
            if (user?.roles?.includes('admin')) {
              runBackfillTimestamps();
            }
          }
        }, async error => {
          if (!isMounted) return;
          console.error('Simple query error:', error);
          // Final fallback: try a one-time getDocs call
          try {
            const fallbackSnapshot = await getDocs(query(collection(db, 'products'), where('sellerId', '==', sellerId)));
            if (!isMounted) return;
            if (fallbackSnapshot.empty) {
              // No products exist — show empty state, not an error
              setSellerProducts([]);
              setProductsError(null);
            } else {
              // Products exist but realtime listener failed
              const productsData = [];
              fallbackSnapshot.forEach(doc => {
                const data = doc.data();
                productsData.push({
                  id: doc.id,
                  title: data.title || '',
                  description: data.shortDescription || data.description || '',
                  price: data.price || 0,
                  category: data.category || 'Other',
                  image: data.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
                  rating: data.rating || 0,
                  sales: 0,
                  revenue: 0,
                  status: data.status || 'published',
                  productFileUrl: data.productFileUrl || data.fileUrl || ''
                });
              });
              setSellerProducts(productsData);
              setProductsError(null);
            }
          } catch (fallbackError) {
            if (!isMounted) return;
            console.error('Final getDocs fallback also failed:', fallbackError);
            // Even the simplest query failed — assume empty for new sellers
            setSellerProducts([]);
            setProductsError(null);
          }
          setLoading(prev => ({
            ...prev,
            products: false
          }));
        });
        // Update the unsubscribe function
        setProductsUnsubscribe(() => unsubscribe);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error setting up simple query:', error);
        // Gracefully show empty state instead of error for new sellers
        setSellerProducts([]);
        setProductsError(null);
        setLoading(prev => ({
          ...prev,
          products: false
        }));
      }
    };
    // Start the query chain with the primary query if we have a valid user
    if (user?.id && activeTab === 'products' || activeTab === 'products_refresh') {
      fetchWithPrimaryQuery(user.id);
    }
    // Return cleanup function for all listeners
    return () => {
      setIsMounted(false);
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (productsUnsubscribe) {
        productsUnsubscribe();
        setProductsUnsubscribe(null);
      }
    };
  }, [isAuthenticated, user, navigate, activeTab]);
  // Apply filters and sorting to products
  const filteredProducts = useMemo(() => {
    let filtered = [...sellerProducts];
    // Apply filters
    if (productFilter !== 'all') {
      filtered = filtered.filter(product => product.status === productFilter);
    }
    // Apply sorting
    switch (productSort) {
      case 'newest':
        // Assuming products are already sorted by creation date
        break;
      case 'oldest':
        filtered = filtered.reverse();
        break;
      case 'price-high':
        filtered = filtered.sort((a, b) => b.price - a.price);
        break;
      case 'price-low':
        filtered = filtered.sort((a, b) => a.price - b.price);
        break;
      case 'sales':
        filtered = filtered.sort((a, b) => b.sales - a.sales);
        break;
    }
    return filtered;
  }, [sellerProducts, productFilter, productSort]);
  // Apply filters and sorting to orders
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];
    // Apply filters
    if (orderFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderFilter);
    }
    // Apply sorting
    switch (orderSort) {
      case 'newest':
        // Assuming orders are already sorted by date
        break;
      case 'oldest':
        filtered = filtered.reverse();
        break;
      case 'amount-high':
        filtered = filtered.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount-low':
        filtered = filtered.sort((a, b) => a.amount - b.amount);
        break;
    }
    return filtered;
  }, [orders, orderFilter, orderSort]);
  // Pagination logic for orders
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  // Filter notifications based on type and search query
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      const matchesFilter = notificationFilter === 'all' || notificationFilter === 'system' && (notification.type === 'approval' || notification.type === 'rejection') || notificationFilter === 'messages' && notification.type === 'message' || notificationFilter === 'payouts' && notification.type === 'payout' || notificationFilter === 'orders' && notification.type === 'order';
      const matchesSearch = notification.message.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [notifications, notificationFilter, searchQuery]);
  const handlePreview = (productId: number | string) => {
    navigate(`/product/${productId}`);
  };
  const handleEdit = (productId: number | string) => {
    setEditingProductId(productId);
    setIsEditing(true);
    setUpdateError(null);
    setUpdateSuccess(null);
  };
  const handleCancelEdit = () => {
    setEditingProductId(null);
    setIsEditing(false);
    setUpdateError(null);
  };
  const handleSaveEdit = async (updatedProduct: Product) => {
    try {
      setUpdateError(null);
      setUpdateSuccess(null);
      // Update product in Firestore
      const productRef = doc(db, 'products', updatedProduct.id.toString());
      // Prepare update data
      const updateData = {
        title: updatedProduct.title,
        description: updatedProduct.description,
        price: updatedProduct.price,
        category: updatedProduct.category,
        status: updatedProduct.status,
        updatedAt: serverTimestamp()
      };
      await updateDoc(productRef, updateData);
      // Success message
      setUpdateSuccess('Product updated successfully!');
      // Reset editing state
      setIsEditing(false);
      setEditingProductId(null);
      // Clear success message after a delay
      setTimeout(() => {
        setUpdateSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating product:', error);
      setUpdateError('Failed to update product. Please try again.');
    }
  };
  const handleDuplicate = async (productId: number | string) => {
    if (!user?.id) return;
    try {
      // Get the original product
      const productRef = doc(db, 'products', productId.toString());
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }
      const productData = productSnap.data();
      // Create a duplicate with "(Copy)" in the title
      const newProduct = {
        ...productData,
        title: `${productData.title} (Copy)`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft' // Set as draft by default
      };
      // Add to Firestore
      await addDoc(collection(db, 'products'), newProduct);
      // Show success message
      setUpdateSuccess('Product duplicated successfully. The copy is in your drafts.');
      // Clear success message after a delay
      setTimeout(() => {
        setUpdateSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error duplicating product:', error);
      setUpdateError('Error duplicating product. Please try again.');
      // Clear error message after a delay
      setTimeout(() => {
        setUpdateError(null);
      }, 3000);
    }
  };
  const handleArchive = async (productId: number | string) => {
    if (confirm('Are you sure you want to archive this product? It will no longer be visible to buyers.')) {
      try {
        // Update product status to archived
        const productRef = doc(db, 'products', productId.toString());
        await updateDoc(productRef, {
          status: 'archived',
          updatedAt: serverTimestamp()
        });
        // Show success message
        setUpdateSuccess('Product archived successfully!');
        // Clear success message after a delay
        setTimeout(() => {
          setUpdateSuccess(null);
        }, 3000);
      } catch (error) {
        console.error('Error archiving product:', error);
        setUpdateError('Failed to archive product. Please try again.');
        // Clear error message after a delay
        setTimeout(() => {
          setUpdateError(null);
        }, 3000);
      }
    }
  };
  // Add new state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async (productId: number | string) => {
    setProductToDelete(productId);
    setIsDeleteModalOpen(true);
  };
  // Update the confirmDelete function to include a direct Firestore deletion fallback
  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      setIsDeleting(true);
      setUpdateError(null);
      setUpdateSuccess(null);
      // First, try the callable function approach
      try {
        const deleteProductCompletely = httpsCallable(functions, 'deleteProductCompletelyCallable');
        const result = await deleteProductCompletely({
          productId: productToDelete
        });
        const data = (result as any).data as {
          success: boolean;
          message?: string;
          summary?: {
            filesDeleted?: number;
            purchasesUpdated?: number;
          };
        };
        if (!data || data.success !== true) {
          throw new Error(data?.message || 'Failed to delete product');
        }
        // Success path
        setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
        let msg = 'Product deleted successfully!';
        if (data.summary) {
          const fd = typeof data.summary.filesDeleted === 'number' ? data.summary.filesDeleted : 0;
          const pu = typeof data.summary.purchasesUpdated === 'number' ? data.summary.purchasesUpdated : 0;
          msg += ` (${fd} files deleted, ${pu} purchases updated)`;
        }
        setUpdateSuccess(msg);
        setIsDeleteModalOpen(false);
        setProductToDelete(null);
        setTimeout(() => setUpdateSuccess(null), 5000);
        return; // Exit early on success
      } catch (callableError) {
        console.error('Callable function error:', callableError);
        // Fall through to HTTP approach if callable fails
      }
      // Second, try the HTTP function approach
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) {
          throw new Error('Authentication required');
        }
        // Replace [YOUR-PROJECT-ID] with your actual Firebase project ID
        const response = await fetch('https://us-central1-[YOUR-PROJECT-ID].cloudfunctions.net/deleteProductCompletelyHttp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            productId: productToDelete
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete product');
        }
        const data = await response.json();
        if (!data || data.success !== true) {
          throw new Error(data?.message || 'Failed to delete product');
        }
        // Success path for HTTP approach
        setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
        let msg = 'Product deleted successfully via HTTP function.';
        if (data.summary) {
          const fd = typeof data.summary.filesDeleted === 'number' ? data.summary.filesDeleted : 0;
          const pu = typeof data.summary.purchasesUpdated === 'number' ? data.summary.purchasesUpdated : 0;
          msg += ` (${fd} files deleted, ${pu} purchases updated)`;
        }
        setUpdateSuccess(msg);
        setIsDeleteModalOpen(false);
        setProductToDelete(null);
        setTimeout(() => setUpdateSuccess(null), 5000);
        return; // Exit early on success
      } catch (httpError) {
        console.error('HTTP function error:', httpError);
        // Fall through to direct Firestore deletion as last resort
      }
      // Last resort: Direct Firestore deletion
      console.log('Attempting direct Firestore deletion as last resort');
      // Delete the product document directly
      await deleteDoc(doc(db, 'products', productToDelete.toString()));
      // Update local state
      setSellerProducts(prev => prev.filter(p => p.id !== productToDelete));
      // Show success message
      setUpdateSuccess('Product deleted successfully via direct Firestore operation.');
      setIsDeleteModalOpen(false);
      setProductToDelete(null);
      setTimeout(() => setUpdateSuccess(null), 5000);
    } catch (e: any) {
      console.error('Delete error:', e);
      let m = 'Failed to delete product. Please try again.';
      const t = String(e?.message || e).toLowerCase();
      if (t.includes('unauthenticated') || t.includes('token')) {
        m = 'You must be logged in to delete a product. Please refresh and try again.';
      } else if (t.includes('permission')) {
        m = 'You do not have permission to delete this product.';
      } else if (t.includes('not found')) {
        m = "The product you're trying to delete could not be found.";
      } else if (t.includes('network') || t.includes('connection')) {
        m = 'Network error. Please check your connection and try again.';
      } else if (t.includes('internal') || t.includes('server')) {
        m = 'Server error. Please try again later.';
      }
      setUpdateError(m);
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setIsDeleting(false);
    }
  };
  // Function to cancel deletion
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
  };
  const handleViewAnalytics = (productId: number | string) => {
    // Set active tab to analytics and store the product ID
    setActiveTab('analytics');
    // You could store the selected product ID in state if needed
  };
  const handleAddNewProduct = () => {
    navigate('/sell-product');
  };
  // Function to navigate to seller's public profile
  const navigateToPublicProfile = () => {
    // Use the current user's ID if it exists in the sample data, otherwise fallback to a default
    const profileId = user?.id || 'designmaster';
    navigate(`/seller/${profileId}`);
  };
  // Function to view order details
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };
  // Function to handle notification action
  const handleNotificationAction = async (notification: Notification) => {
    try {
      // Mark notification as read
      await markNotificationAsRead(notification.id);
      // Update UI (this will happen automatically via the real-time listener)
      // Handle action
      if (notification.cta) {
        if (notification.cta.action === 'view-product' && notification.cta.target) {
          // Navigate to product page
          navigate(`/product/${notification.cta.target}`);
        } else if (notification.cta.action === 'view-payout') {
          // Set active tab to payouts
          setActiveTab('payouts');
        } else if (notification.cta.action === 'reply' && notification.cta.target) {
          // Set active tab to messages with specific conversation
          setActiveTab('messages');
          // You could set a state variable to open the specific conversation
        } else if (notification.cta.action === 'fix-product' && notification.cta.target) {
          // Navigate to edit product page
          navigate(`/sell-product?edit=${notification.cta.target}`);
        } else if (notification.cta.action === 'view-order' && notification.cta.target) {
          // Set active tab to orders and find the specific order
          setActiveTab('orders');
          const order = orders.find(o => o.id === notification.cta?.target);
          if (order) {
            handleViewOrder(order);
          }
        }
      }
    } catch (error) {
      console.error('Error handling notification action:', error);
    }
  };
  // Function to handle page change
  const changePage = (page: number) => {
    setCurrentPage(page);
  };
  // Function to request a payout
  const handleRequestPayout = async (amount: number) => {
    if (!user?.id) return;
    try {
      // Create a new payout request in Firestore
      const payoutRef = collection(db, 'payouts');
      await addDoc(payoutRef, {
        sellerId: user.id,
        amount: amount,
        status: 'pending',
        paymentMethod: 'PayPal',
        createdAt: new Date(),
        transactionId: '' // Will be filled when processed
      });
      // Update available balance (this will happen automatically via the real-time listener)
      // Create a notification for the user
      const notificationRef = collection(db, 'notifications');
      await addDoc(notificationRef, {
        userId: user.id,
        type: 'payout',
        message: `Your payout request for $${amount.toFixed(2)} has been submitted and is being processed.`,
        read: false,
        createdAt: new Date(),
        cta: {
          label: 'View Payout',
          action: 'view-payout'
        }
      });
      // Show success message
      alert(`Your payout request for $${amount.toFixed(2)} has been submitted successfully.`);
    } catch (error) {
      console.error('Error requesting payout:', error);
      throw error;
    }
  };
  // Function to manually retry loading products
  const handleRetryLoadProducts = () => {
    // Reset error state
    setProductsError(null);
    // Reset query attempt counter
    setQueryAttempt(0);
    // Set loading state
    setLoading(prev => ({
      ...prev,
      products: true
    }));
    // Force refresh by toggling activeTab
    setActiveTab(activeTab === 'products' ? 'products_refresh' : 'products');
  };
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-7033">
      <Navbar scrolled={scrolled} data-id="element-7034" />
      <main className="pt-24 pb-16" data-id="element-7035">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7036">
          {/* Seller Dashboard Header */}
          <div className="mb-8" data-id="element-7037">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-7038">
              <div data-id="element-7039">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7040">
                  Seller Dashboard
                </h1>
                <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-7041">
                  Manage your products, track sales, and grow your business
                </p>
              </div>
            </div>
          </div>
          {/* Seller Profile */}
          <div className="bg-white dark:bg-[#162825] rounded-xl p-6 shadow-sm mb-8" data-id="element-7042">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-7043">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-3xl font-bold" data-id="element-7044">
                {user?.name?.charAt(0) || 'S'}
              </div>
              <div className="flex-grow" data-id="element-7045">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-7046">
                  {user?.name || 'Seller Name'}
                </h2>
                <p className="text-gray-500 dark:text-[#7A8F8A] mb-3" data-id="element-7047">
                  {user?.email || 'seller@example.com'}
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-7048">
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-7049">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7050">
                      Total Products
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-7051">
                      {sellerProducts.length}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-7052">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7053">
                      Total Sales
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-7054">
                      {orders.filter(order => order.status === 'paid').length}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-7055">
                    <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7056">
                      Total Revenue
                    </p>
                    <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-7057">
                      ${totalEarnings.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-7058">
                <button onClick={navigateToPublicProfile} className="bg-white dark:bg-[#1A3330] border border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2DB489]/5 dark:hover:bg-[#4ED1A0]/10 transition-all duration-300 hover:scale-103 flex items-center whitespace-nowrap" data-id="element-7059">
                  <EyeIcon className="h-4 w-4 mr-2" data-id="element-7060" />
                  View Public Profile
                </button>
              </div>
            </div>
          </div>
          {/* Dashboard Tabs */}
          <div className="mb-8 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-7061">
            <nav className="flex space-x-8 overflow-x-auto" data-id="element-7062">
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'products' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('products')} data-id="element-7063">
                <PackageIcon className="h-4 w-4 inline mr-2" data-id="element-7064" />
                My Listings
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'orders' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('orders')} data-id="element-7065">
                <ShoppingCartIcon className="h-4 w-4 inline mr-2" data-id="element-7066" />
                Orders
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payouts' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('payouts')} data-id="element-7067">
                <CreditCardIcon className="h-4 w-4 inline mr-2" data-id="element-7068" />
                Payouts
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'notifications' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('notifications')} data-id="element-7069">
                <BellIcon className="h-4 w-4 inline mr-2" data-id="element-7070" />
                Notifications
                {notifications.filter(n => !n.isRead).length > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full" data-id="element-7071">
                    {notifications.filter(n => !n.isRead).length}
                  </span>}
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'analytics' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('analytics')} data-id="element-7072">
                <BarChart3Icon className="h-4 w-4 inline mr-2" data-id="element-7073" />
                Analytics
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'messages' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('messages')} data-id="element-7074">
                <MessageSquareIcon className="h-4 w-4 inline mr-2" data-id="element-7075" />
                Messages
              </button>
              {hasAffiliateRole && <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'affiliate' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => navigate('/affiliate-dashboard')} data-id="element-7076">
                  <DollarSignIcon className="h-4 w-4 inline mr-2" data-id="element-7077" />
                  Affiliate Dashboard
                </button>}
            </nav>
          </div>
          {/* Tab Content */}
          {activeTab === 'products' && <div data-id="element-7078">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-7079">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-7080">
                  My Listings
                </h2>
                <div className="flex items-center space-x-3" data-id="element-7081">
                  <div className="relative" data-id="element-7082">
                    <select value={productSort} onChange={e => setProductSort(e.target.value)} className="appearance-none bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] rounded-lg py-2 px-4 pr-8 leading-tight focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent text-gray-700 dark:text-[#F8FAF9]" data-id="element-7083">
                      <option value="newest" data-id="element-7084">Newest First</option>
                      <option value="oldest" data-id="element-7085">Oldest First</option>
                      <option value="price-high" data-id="element-7086">Price: High to Low</option>
                      <option value="price-low" data-id="element-7087">Price: Low to High</option>
                      <option value="popular" data-id="element-7088">Most Popular</option>
                      <option value="alphabetical" data-id="element-7089">Alphabetical</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-[#F8FAF9]" data-id="element-7090">
                      <ChevronDownIcon className="h-4 w-4" data-id="element-7091" />
                    </div>
                  </div>
                  <button onClick={handleAddNewProduct} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] transition-colors" data-id="element-7092">
                    <PlusIcon className="h-5 w-5 mr-1" data-id="element-7093" />
                    Add New Product
                  </button>
                </div>
              </div>
              {/* Success/Error Messages */}
              {updateSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-7094">
                  <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-7095" />
                  <span data-id="element-7096">{updateSuccess}</span>
                </div>}
              {updateError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-7097">
                  <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-7098" />
                  <span data-id="element-7099">{updateError}</span>
                </div>}
              {/* Products Loading State */}
              {loading.products ? <div className="flex justify-center items-center py-20" data-id="element-7100">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-7101"></div>
                </div> : productsError ? <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm text-center" data-id="element-7102">
                  <AlertCircleIcon className="h-16 w-16 mx-auto text-red-500 dark:text-red-400 mb-4" data-id="element-7103" />
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7104">
                    Error Loading Products
                  </h3>
                  <p className="text-gray-500 dark:text-[#B0C1BD] max-w-md mx-auto mb-6" data-id="element-7105">
                    {productsError}
                  </p>
                  <button onClick={handleRetryLoadProducts} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg hover:bg-opacity-90 transition-colors" data-id="element-7106">
                    <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-7107" />
                    Try Again
                  </button>
                </div> : filteredProducts.length > 0 ? <div data-id="element-7108">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-7109">
                    {filteredProducts.map(product => <ProductCard key={product.id} product={product} onPreview={handlePreview} onEdit={handleEdit} onDuplicate={handleDuplicate} onDelete={handleDelete} onViewAnalytics={handleViewAnalytics} onArchive={handleArchive} isEditing={isEditing} editingProductId={editingProductId} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} data-id="element-7110" />)}
                  </div>
                </div> : <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm text-center" data-id="element-7111">
                  <PackageIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-[#1A3330] mb-4" data-id="element-7112" />
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7113">
                    No Products Yet
                  </h3>
                  <p className="text-gray-500 dark:text-[#B0C1BD] max-w-md mx-auto mb-6" data-id="element-7114">
                    You haven't created any products yet. Start selling by
                    adding your first product.
                  </p>
                  <button onClick={handleAddNewProduct} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg hover:bg-opacity-90 transition-colors" data-id="element-7115">
                    <PlusIcon className="h-5 w-5 mr-2" data-id="element-7116" />
                    Add New Product
                  </button>
                </div>}
            </div>}
          {activeTab === 'orders' && <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-7117">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7118">
                <ShoppingCartIcon className="h-8 w-8" data-id="element-7119" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7120">
                No Orders Yet
              </h3>
              <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-7121">
                Orders from customers will appear here once you start selling.
              </p>
            </div>}
          {activeTab === 'payouts' && <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-7122">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7123">
                <WalletIcon className="h-8 w-8" data-id="element-7124" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7125">
                No Payouts Yet
              </h3>
              <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-7126">
                Your earnings and payout requests will appear here.
              </p>
            </div>}
          {activeTab === 'notifications' && <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-7127">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7128">
                <BellIcon className="h-8 w-8" data-id="element-7129" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7130">
                No Notifications
              </h3>
              <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-7131">
                Important updates about your store will appear here.
              </p>
            </div>}
          {activeTab === 'analytics' && <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-7132">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7133">
                <BarChart3Icon className="h-8 w-8" data-id="element-7134" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7135">
                Analytics Coming Soon
              </h3>
              <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-7136">
                Analytics will appear once your products start generating sales.
              </p>
            </div>}
          {activeTab === 'messages' && <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-7137">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7138">
                <MessageSquareIcon className="h-8 w-8" data-id="element-7139" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-7140">
                No Messages Yet
              </h3>
              <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-7141">
                Customers will be able to contact you here.
              </p>
            </div>}
        </div>
      </main>
      <Footer data-id="element-7142" />
      {/* Add the delete confirmation modal */}
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={cancelDelete} onConfirm={confirmDelete} isLoading={isDeleting} title="Delete Product Permanently" message="Are you sure you want to permanently delete this product? This action cannot be undone and will remove all associated files. Purchase records will be preserved but marked as referencing a deleted product." data-id="element-7143" />
    </div>;
};